const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

export function duration(seconds: number, compact = false): string {
  const safe = Math.max(0, Math.round(seconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  if (compact) return hours > 0 ? `${hours}ч ${String(minutes).padStart(2, '0')}м` : `${minutes} мин`
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function signedDuration(seconds: number): string {
  if (seconds === 0) return '±0 мин'
  return `${seconds > 0 ? '+' : '−'}${duration(Math.abs(seconds), true)}`
}

export function clock(value?: string | Date, timeZone?: string): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zoneFor(value, timeZone),
  }).format(asDate(value))
}

export function fullDate(value: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric', month: 'long', weekday: 'long', timeZone: zoneFor(value, timeZone),
  }).format(asDate(value))
}

export function shortDate(value: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric', month: 'short', timeZone: zoneFor(value, timeZone),
  }).format(asDate(value))
}

export function dateTime(value: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    timeZone: zoneFor(value, timeZone),
  }).format(asDate(value))
}

export function minutesAsClock(value?: number | null): string {
  if (value === undefined || value === null) return '—'
  const rounded = Math.round(value)
  return `${String(Math.floor(rounded / 60)).padStart(2, '0')}:${String(rounded % 60).padStart(2, '0')}`
}

export function localIsoDate(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

/** Returns the calendar date for an instant in the account time zone. */
export function isoDateInTimeZone(value: Date, timeZone: string): string {
  const parts = zonedParts(value, timeZone)
  return `${parts.year}-${two(parts.month)}-${two(parts.day)}`
}

/** Shifts an ISO calendar date without involving the device time zone. */
export function shiftIsoDate(value: string, days: number): string {
  const [year, month, day] = value.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, month - 1, day + days, 12))
  return `${shifted.getUTCFullYear()}-${two(shifted.getUTCMonth() + 1)}-${two(shifted.getUTCDate())}`
}

/** Formats an instant for datetime-local in the requested account time zone. */
export function toDateTimeLocal(value: string, timeZone?: string): string {
  if (!timeZone) {
    const date = asDate(value)
    const offset = date.getTimezoneOffset() * 60_000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
  }
  const parts = zonedParts(asDate(value), timeZone)
  return `${parts.year}-${two(parts.month)}-${two(parts.day)}T${two(parts.hour)}:${two(parts.minute)}`
}

/** Converts a datetime-local value interpreted in the account time zone to an instant. */
export function fromDateTimeLocal(value: string, timeZone?: string): string {
  if (!timeZone) return new Date(value).toISOString()
  const match = LOCAL_DATE_TIME.exec(value)
  if (!match) throw new Error('Проверьте дату и время')
  const desired = {
    year: Number(match[1]), month: Number(match[2]), day: Number(match[3]),
    hour: Number(match[4]), minute: Number(match[5]), second: Number(match[6] || 0),
  }
  const desiredAsUtc = Date.UTC(desired.year, desired.month - 1, desired.day, desired.hour, desired.minute, desired.second)
  let candidate = desiredAsUtc
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const actual = zonedParts(new Date(candidate), timeZone)
    const actualAsUtc = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second)
    const adjustment = desiredAsUtc - actualAsUtc
    if (adjustment === 0) break
    candidate += adjustment
  }
  const actual = zonedParts(new Date(candidate), timeZone)
  if (actual.year !== desired.year || actual.month !== desired.month || actual.day !== desired.day
    || actual.hour !== desired.hour || actual.minute !== desired.minute) {
    throw new Error('Такого местного времени нет в выбранном часовом поясе')
  }
  return new Date(candidate).toISOString()
}

function asDate(value: string | Date): Date {
  if (value instanceof Date) return value
  if (DATE_ONLY.test(value)) return new Date(`${value}T12:00:00.000Z`)
  return new Date(value)
}

function zoneFor(value: string | Date, timeZone?: string) {
  return typeof value === 'string' && DATE_ONLY.test(value) ? 'UTC' : timeZone
}

function zonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date)
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value)
  return {
    year: value('year'), month: value('month'), day: value('day'),
    hour: value('hour'), minute: value('minute'), second: value('second'),
  }
}

function two(value: number) {
  return String(value).padStart(2, '0')
}
